import React, { useRef } from "react";
import styles from "./CustomFiles.module.css";

const CustomFileInput = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  accept = "*",
  multiple = false,
  disabled = false,
  required = false,
  error,
  helperText,
  placeholder = "No file chosen",
  buttonText = "Choose File",
  className = "",
  labelClassName = "",
  maxSize,
  onError,
  ...rest
}) => {
  const inputRef = useRef(null);

  const handleClick = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleChange = (e) => {
    const files = e.target.files;

    if (files && files.length > 0) {
      if (maxSize) {
        const invalidFiles = Array.from(files).filter(
          (file) => file.size > maxSize,
        );
        if (invalidFiles.length > 0) {
          if (onError) {
            onError(`File size exceeds ${formatFileSize(maxSize)} limit`);
          }
          e.target.value = "";
          return;
        }
      }

      if (onChange) {
        if (multiple) {
          onChange(Array.from(files), name);
        } else {
          onChange(files[0], name);
        }
      }
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (onChange) {
      onChange(multiple ? [] : null, name);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getDisplayName = () => {
    if (!value) return placeholder;

    if (multiple && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) return value[0].name;
      return `${value.length} files selected`;
    }

    return value.name || placeholder;
  };

  const hasValue = multiple
    ? Array.isArray(value) && value.length > 0
    : value !== null && value !== undefined;

  return (
    <div className={`${styles.fileInputWrapper} ${className}`} {...rest}>
      {label && (
        <label
          htmlFor={name}
          className={`${styles.label} ${labelClassName} ${
            disabled ? styles.labelDisabled : ""
          }`}
        >
          {label}
          {required && <span className={styles.required}>*</span>}
        </label>
      )}

      <div
        className={`${styles.fileInputContainer} ${
          error ? styles.containerError : ""
        } ${disabled ? styles.containerDisabled : ""}`}
      >
        <input
          ref={inputRef}
          type="file"
          id={name}
          name={name}
          onChange={handleChange}
          onBlur={onBlur}
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          className={styles.fileInput}
          aria-invalid={!!error}
          aria-describedby={error || helperText ? `${name}-helper` : undefined}
        />

        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className={styles.fileButton}
        >
          <i className="fa-solid fa-upload" />
          {buttonText}
        </button>

        <span
          className={`${styles.fileName} ${hasValue ? styles.fileNameActive : ""}`}
        >
          {getDisplayName()}
        </span>

        {hasValue && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className={styles.clearButton}
            aria-label="Clear file"
          >
            <i className="fa-solid fa-times" />
          </button>
        )}
      </div>

      {(error || helperText) && (
        <span
          id={`${name}-helper`}
          className={`${styles.helperText} ${error ? styles.errorText : ""}`}
        >
          {error || helperText}
        </span>
      )}
    </div>
  );
};

export default CustomFileInput;
